import { Injectable, type WritableSignal, inject, signal } from '@angular/core';
import {
  STORAGE_KEYS,
  type StorageKey,
  type StorageKeyType,
} from '@shared/models/storage';
import { WINDOW } from '@tvd/cdk/browser';

@Injectable({ providedIn: 'root' })
export class Storage {
  readonly #winRef = inject(WINDOW);
  readonly #storage = this.#winRef.localStorage;

  get<K extends StorageKey, T extends StorageKeyType<K>>(key: K): T | null {
    const value = this.#storage.getItem(STORAGE_KEYS[key].key);
    if (value) {
      try {
        return JSON.parse(value) as T;
      } catch {
        return value as T;
      }
    }
    return null;
  }

  getAsSignal<K extends StorageKey>(
    key: K,
    { initialValue }: { initialValue: StorageKeyType<K> },
  ): WritableSignal<StorageKeyType<K>>;
  getAsSignal<K extends StorageKey>(
    key: K,
    options?: { initialValue?: StorageKeyType<K> },
  ): WritableSignal<StorageKeyType<K> | null>;
  getAsSignal<K extends StorageKey>(
    key: K,
    { initialValue }: { initialValue?: StorageKeyType<K> } = {},
  ):
    | WritableSignal<StorageKeyType<K>>
    | WritableSignal<StorageKeyType<K> | null> {
    const initial = this.get(key) ?? initialValue ?? null;
    const storageSignal = signal(
      initial,
    ) as WritableSignal<StorageKeyType<K> | null>;

    const originalSet = storageSignal.set.bind(storageSignal);

    storageSignal.set = (value: StorageKeyType<K> | null) => {
      if (value === null) {
        this.delete(key);
      } else {
        this.set(key, value);
      }
      originalSet(value);
    };

    storageSignal.update = (
      updateFn: (value: StorageKeyType<K> | null) => StorageKeyType<K> | null,
    ) => {
      const currentValue = storageSignal();
      const newValue = updateFn(currentValue);
      if (newValue === null) {
        this.delete(key);
      } else {
        this.set(key, newValue);
      }
      originalSet(newValue);
    };

    return storageSignal;
  }

  set<K extends StorageKey, T extends StorageKeyType<K>>(key: K, value: T) {
    this.#storage.setItem(
      STORAGE_KEYS[key].key,
      typeof value === 'string' ? value : JSON.stringify(value),
    );
  }

  delete<K extends StorageKey>(key: K) {
    this.#storage.removeItem(STORAGE_KEYS[key].key);
  }

  clear<K extends StorageKey>(exceptions: K[] = []) {
    if (!exceptions.length) {
      this.#storage.clear();
      return;
    }
    for (const key of Object.keys(this.#storage)) {
      if (exceptions.some((k) => STORAGE_KEYS[k].key === key)) {
        continue;
      }
      this.#storage.removeItem(key as string);
    }
  }
}
